/* eslint no-param-reassign: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import { css, StyleSheet } from 'aphrodite';
import { TSS_BLACK, TSS_GREEN, TSS_RED, TSS_BLUE_1 } from 'tss-core-lib-common';

var styles = void 0;

var HEIGHT_REM = 1.7;
var BAR_HEIGHT_REM = HEIGHT_REM - 1;

var StabilityIntervalThermometer = function StabilityIntervalThermometer(_ref) {
  var unlimited = _ref.unlimited,
      minutesAllowed = _ref.minutesAllowed,
      minutesUsed = _ref.minutesUsed,
      adjustedMinutes = _ref.adjustedMinutes;

  if (minutesUsed < 0) {
    minutesAllowed += Math.abs(minutesUsed);
    minutesUsed = 0;
  }

  var totalAllowedMinutes = minutesAllowed + adjustedMinutes;
  var scale = (minutesUsed < 0 ? (Math.abs(minutesUsed) + minutesAllowed) * 2 : Math.max((minutesAllowed + Math.max(0, adjustedMinutes)) * 2, Math.abs(minutesUsed))) || 1;
  var allowedMinutesRatio = Math.max(0, scale / 2 - minutesAllowed) * (minutesUsed >= 0 ? 1 : -1);

  var tempOkMinutes = Math.max(0, Math.min(minutesUsed, minutesAllowed, minutesUsed + adjustedMinutes, minutesAllowed + adjustedMinutes));

  var tempAdjustedOkMinutes = Math.max(0, Math.min(adjustedMinutes, minutesUsed - minutesAllowed));

  var tempOkNotUsedMinutes = Math.max(0, totalAllowedMinutes - (tempOkMinutes + tempAdjustedOkMinutes));

  var tempNotOkMinutes = Math.min(Math.max(0, minutesUsed - totalAllowedMinutes), scale);

  var tempLeftoverMinutes = Math.min(Math.max(0, scale - tempOkMinutes - tempAdjustedOkMinutes - tempOkNotUsedMinutes - tempNotOkMinutes), scale);

  var beakerWidth = unlimited ? 0.5 : 0.5 - allowedMinutesRatio / scale;
  var adjustedBeakerWidth = Math.max(0, 0.5 - Math.max(0, scale / 2 - adjustedMinutes) / scale);
  var adjustedBeakerMargin = adjustedMinutes < 0 ? 0.5 - Math.max(0, scale / 2 - Math.abs(adjustedMinutes)) / scale : Math.max(0, 0.5 - beakerWidth);

  var tempOkWidth = unlimited ? 0 : tempOkMinutes / scale;
  var tempAdjustedOkWidth = unlimited ? 0 : tempAdjustedOkMinutes / scale;
  var tempMiddleWidth = unlimited ? 0.5 : tempOkNotUsedMinutes / scale;
  var tempNotOkWidth = unlimited ? 0 : tempNotOkMinutes / scale;
  var tempLeftoverWidth = unlimited ? 0.5 : tempLeftoverMinutes / scale;

  return React.createElement(
    'div',
    { className: css(styles.container) },
    React.createElement('div', {
      key: 'beakerWidth',
      className: css(styles.beaker),
      style: { width: 'calc(' + Math.max(beakerWidth, 0.01) * 100 + '% + ' + HEIGHT_REM / 2 + 'rem)' }
    }),
    adjustedMinutes ? React.createElement('div', {
      key: 'adjustedBeakerWidth',
      className: css(styles.beaker, styles.adjustedBeaker),
      style: {
        width: adjustedBeakerWidth * 100 + '%',
        marginLeft: adjustedBeakerMargin * -100 + '%'
      }
    }) : null,
    React.createElement('div', { key: 'tempOkWidth', className: css(styles.tempOkBar), style: { width: tempOkWidth * 100 + '%' } }),
    React.createElement('div', {
      key: 'tempAdjustedOkWidth',
      className: css(styles.tempAdjustedOkBar),
      style: { width: tempAdjustedOkWidth * 100 + '%' }
    }),
    React.createElement('div', { key: 'tempMiddleWidth', className: css(styles.tempMiddleBar), style: { width: tempMiddleWidth * 100 + '%' } }),
    React.createElement('div', { key: 'tempNotOkWidth', className: css(styles.tempNotOkBar), style: { width: tempNotOkWidth * 100 + '%' } }),
    React.createElement('div', {
      key: 'tempLeftoverWidth',
      className: css(styles.tempLeftOverBar, unlimited && styles.tempUnlimitedBar),
      style: { width: tempLeftoverWidth * 100 + '%' }
    })
  );
};

StabilityIntervalThermometer.propTypes = {
  unlimited: PropTypes.bool,
  minutesAllowed: PropTypes.number.isRequired,
  minutesUsed: PropTypes.number.isRequired,
  adjustedMinutes: PropTypes.number.isRequired
};

StabilityIntervalThermometer.defaultProps = {
  unlimited: false
};

styles = StyleSheet.create({
  container: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    height: HEIGHT_REM + 'rem'
  },

  beaker: {
    position: 'absolute',
    left: -HEIGHT_REM / 2 + 'rem',
    top: 0,
    height: HEIGHT_REM + 'rem',
    border: '1px solid ' + TSS_BLACK(),
    borderWidth: '0.2rem 0 0.2rem 0.2rem',
    borderRadius: '90rem 0 0 90rem',
    backgroundColor: TSS_BLUE_1(),

    ':after': {
      content: '""',
      position: 'absolute',
      backgroundColor: TSS_BLACK(),
      top: '100%',
      right: 0,
      height: '0.9rem',
      width: 2
    },

    ':before': {
      content: '""',
      position: 'absolute',
      backgroundColor: TSS_BLACK(),
      bottom: '100%',
      right: 0,
      height: '0.9rem',
      width: 2
    }
  },

  adjustedBeaker: {
    left: '50%',
    opacity: 0.2,
    borderRadius: 0,
    borderWidth: '0.2rem 0'
  },

  tempOkBar: {
    position: 'relative',
    zIndex: 1,
    height: BAR_HEIGHT_REM + 'rem',
    backgroundColor: TSS_GREEN(),
    border: 0,
    borderRadius: '900px 0 0 900px'
  },

  tempAdjustedOkBar: {
    position: 'relative',
    zIndex: 1,
    height: BAR_HEIGHT_REM + 'rem',
    backgroundColor: TSS_BLACK(0.5)
  },

  tempMiddleBar: {
    position: 'relative',
    zIndex: 1,
    height: BAR_HEIGHT_REM + 'rem',
    backgroundColor: 'transparent'
  },

  tempNotOkBar: {
    position: 'relative',
    zIndex: 1,
    height: BAR_HEIGHT_REM + 'rem',
    border: 0,
    borderRadius: '0 900px 900px 0',
    backgroundColor: TSS_RED()
  },

  tempUnlimitedBar: {
    backgroundColor: TSS_BLUE_1()
  },

  tempLeftOverBar: {
    position: 'relative',
    zIndex: 1,
    backgroundColor: TSS_RED(0.2),
    border: 0,
    borderRadius: '0 900px 900px 0',
    height: BAR_HEIGHT_REM + 'rem'
  }
});

export default StabilityIntervalThermometer;